import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useDungeonEditorStore } from '@/store/dungeonEditorStore';
import type { DungeonTrigger } from '@/lib/db/dungeon-types';

const NODE_W = 200;
const NODE_H = 50;
const GAP_X = 60;
const GAP_Y = 30;

const EFFECT_COLORS: Record<DungeonTrigger['effect'], string> = {
  dialogue: '#22c55e',
  combat: '#ef4444',
  trap: '#f59e0b',
  teleport: '#3b82f6',
  spawn: '#8b5cf6',
  custom: '#ec4899',
};

interface NodeLayout {
  trigger: DungeonTrigger;
  x: number;
  y: number;
}

function buildLayout(triggers: DungeonTrigger[]): NodeLayout[] {
  // Build flag -> triggerId map (which trigger sets which flag)
  const flagSetterMap = new Map<string, string>();
  for (const t of triggers) {
    if (t.setsFlag) {
      flagSetterMap.set(t.setsFlag, t.id);
    }
  }

  // BFS to compute depths
  const depths = new Map<string, number>();
  const queue: string[] = [];

  // Start with triggers that have no required flags
  for (const t of triggers) {
    if (t.requiredFlags.length === 0) {
      depths.set(t.id, 0);
      queue.push(t.id);
    }
  }

  const triggerMap = new Map(triggers.map((t) => [t.id, t]));

  let head = 0;
  while (head < queue.length) {
    const currentId = queue[head++];
    const currentDepth = depths.get(currentId) ?? 0;
    const current = triggerMap.get(currentId);
    if (!current || !current.setsFlag) continue;

    // Find all triggers that require this trigger's flag
    for (const t of triggers) {
      if (t.requiredFlags.includes(current.setsFlag) && !depths.has(t.id)) {
        depths.set(t.id, currentDepth + 1);
        queue.push(t.id);
      }
    }
  }

  // Assign unvisited triggers to depth 0
  for (const t of triggers) {
    if (!depths.has(t.id)) {
      depths.set(t.id, 0);
    }
  }

  // Group by depth
  const byDepth = new Map<number, DungeonTrigger[]>();
  for (const t of triggers) {
    const d = depths.get(t.id) ?? 0;
    if (!byDepth.has(d)) byDepth.set(d, []);
    byDepth.get(d)!.push(t);
  }

  const layout: NodeLayout[] = [];
  const sortedDepths = Array.from(byDepth.keys()).sort((a, b) => a - b);

  for (const depth of sortedDepths) {
    const col = byDepth.get(depth)!;
    for (let row = 0; row < col.length; row++) {
      layout.push({
        trigger: col[row],
        x: depth * (NODE_W + GAP_X) + 20,
        y: row * (NODE_H + GAP_Y) + 20,
      });
    }
  }

  return layout;
}

function drawRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

export function TriggerGraphModal({ onClose }: { onClose: () => void }) {
  const triggers = useDungeonEditorStore((s) => s.mapData.triggers);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.scale(dpr, dpr);

    const W = rect.width;
    const H = rect.height;

    // Background
    ctx.fillStyle = '#0d0d1a';
    ctx.fillRect(0, 0, W, H);

    if (triggers.length === 0) {
      ctx.fillStyle = '#6b7280';
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('No triggers defined', W / 2, H / 2);
      return;
    }

    const layout = buildLayout(triggers);
    const nodeMap = new Map(layout.map((n) => [n.trigger.id, n]));

    // Build flag -> setter node map for edges
    const flagSetterMap = new Map<string, string>();
    for (const t of triggers) {
      if (t.setsFlag) {
        flagSetterMap.set(t.setsFlag, t.id);
      }
    }

    // Draw edges
    ctx.strokeStyle = '#a855f744';
    ctx.lineWidth = 1.5;

    for (const node of layout) {
      for (const flag of node.trigger.requiredFlags) {
        const setterId = flagSetterMap.get(flag);
        if (!setterId) continue;
        const from = nodeMap.get(setterId);
        const to = node;
        if (!from || !to) continue;

        const fx = from.x + NODE_W;
        const fy = from.y + NODE_H / 2;
        const tx = to.x;
        const ty = to.y + NODE_H / 2;
        const cx = (fx + tx) / 2;

        ctx.beginPath();
        ctx.moveTo(fx, fy);
        ctx.bezierCurveTo(cx, fy, cx, ty, tx, ty);
        ctx.stroke();

        // Arrowhead
        ctx.fillStyle = '#a855f744';
        ctx.beginPath();
        ctx.moveTo(tx, ty);
        ctx.lineTo(tx - 8, ty - 4);
        ctx.lineTo(tx - 8, ty + 4);
        ctx.closePath();
        ctx.fill();
      }
    }

    // Draw nodes
    for (const { trigger, x, y } of layout) {
      const borderColor = EFFECT_COLORS[trigger.effect] ?? '#6b7280';

      // Node background
      drawRoundedRect(ctx, x, y, NODE_W, NODE_H, 6);
      ctx.fillStyle = '#1e1e2e';
      ctx.fill();

      // Colored border
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = 1.5;
      drawRoundedRect(ctx, x, y, NODE_W, NODE_H, 6);
      ctx.stroke();

      // Name
      ctx.fillStyle = '#f3f4f6';
      ctx.font = 'bold 11px sans-serif';
      ctx.textAlign = 'left';
      const name = trigger.name.length > 22 ? trigger.name.slice(0, 22) + '…' : trigger.name;
      ctx.fillText(name, x + 8, y + 16);

      // Condition → effect line
      ctx.fillStyle = '#9ca3af';
      ctx.font = '9px sans-serif';
      const condEffect = `${trigger.condition} → ${trigger.effect}`;
      ctx.fillText(condEffect, x + 8, y + 29);

      // setsFlag
      if (trigger.setsFlag) {
        ctx.fillStyle = '#a855f7';
        ctx.font = '9px sans-serif';
        const flagText =
          trigger.setsFlag.length > 24 ? trigger.setsFlag.slice(0, 24) + '…' : trigger.setsFlag;
        ctx.fillText(`sets: ${flagText}`, x + 8, y + 42);
      }
    }
  }, [triggers]);

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-900 border-b border-gray-800 flex-shrink-0">
        <h2 className="text-sm font-semibold text-gray-100">Trigger Graph</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-100 transition-colors"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>

      {/* Canvas */}
      <canvas ref={canvasRef} className="flex-1 w-full h-full" />
    </div>
  );
}
